import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useBackNavigation } from "../use-back-navigation";

let dialogCount = 0;
let originalOverflow = "";

export function PageDialog({
  title,
  onClose,
  busy = false,
  children,
  action,
}: {
  title: string;
  onClose(): void;
  busy?: boolean;
  children: ReactNode;
  action?: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const state = useRef({ onClose, busy });
  state.current = { onClose, busy };
  const id = useId();
  useBackNavigation(onClose, !busy, ref);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (dialogCount === 0) originalOverflow = document.body.style.overflow;
    dialogCount++;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    const key = (event: KeyboardEvent) => {
      // Loading/retry can remove the focused control. Keep Escape and Tab working
      // even when focus falls back to the body, and handle only the top dialog.
      const dialogs = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'));
      if (dialogs.at(-1) !== ref.current) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        if (!state.current.busy) state.current.onClose();
      }
      if (event.key !== "Tab") return;
      const nodes = Array.from(
        ref.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]',
        ) ?? [],
      ).filter((node) => node.getClientRects().length);
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const focusOutside = document.activeElement === ref.current || !ref.current?.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || focusOutside)) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !ref.current?.contains(document.activeElement))
      ) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      dialogCount--;
      if (dialogCount === 0) document.body.style.overflow = originalOverflow;
      previous?.focus();
    };
  }, []);
  return createPortal(
    <div className="reference-layer">
      <section ref={ref} tabIndex={-1} className="reference-page" role="dialog" aria-modal="true" aria-labelledby={id}>
        <header className="reference-header">
          <button type="button" disabled={busy} onClick={onClose} aria-label="Назад">
            ‹
          </button>
          <h2 id={id}>{title}</h2>
          <div>{action}</div>
        </header>
        <div className="reference-body">{children}</div>
      </section>
    </div>,
    document.body,
  );
}
export function MenuRow({
  label,
  value,
  icon,
  onClick,
  children,
}: {
  label: string;
  value?: string;
  icon?: string;
  onClick?: () => void;
  children?: ReactNode;
}) {
  const inner = (
    <>
      <span className="reference-row-icon" aria-hidden="true">
        {icon ?? ""}
      </span>
      <span className="reference-row-label">{label}</span>
      {children ?? (
        <>
          <span className="reference-row-value">{value}</span>
          <span aria-hidden="true">›</span>
        </>
      )}
    </>
  );
  return onClick ? (
    <button type="button" className="reference-row" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className="reference-row">{inner}</div>
  );
}
export function ChoiceList({
  title,
  options,
  value,
  onSelect,
  onClose,
  searchable = false,
}: {
  title: string;
  options: Array<{ value: string; label: string; description?: string }>;
  value: string | null;
  onSelect(value: string | null): void;
  onClose(): void;
  searchable?: boolean;
}) {
  const [search, setSearch] = useState("");
  return (
    <PageDialog title={title} onClose={onClose}>
      {searchable && (
        <input
          className="reference-input"
          type="search"
          placeholder="Поиск"
          aria-label="Поиск"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="reference-group choice-list">
        <button type="button" aria-pressed={value === null} onClick={() => onSelect(null)}>
          Не указывать {value === null && "✓"}
        </button>
        {options
          .filter((o) => o.label.toLocaleLowerCase("ru").includes(search.toLocaleLowerCase("ru")))
          .map((o) => (
            <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onSelect(o.value)}>
              <span>
                <strong>{o.label}</strong>
                {o.description && <small>{o.description}</small>}
              </span>
              <span>{value === o.value && "✓"}</span>
            </button>
          ))}
      </div>
    </PageDialog>
  );
}
export function RangePair({
  title,
  min,
  max,
  low,
  high,
  onChange,
  unit = "",
}: {
  title: string;
  min: number;
  max: number;
  low: number;
  high: number;
  unit?: string;
  onChange(low: number, high: number): void;
}) {
  return (
    <fieldset className="reference-range">
      <legend>{title}</legend>
      <output>
        {low} – {high}
        {unit}
      </output>
      <div>
        <input
          type="range"
          aria-label={`${title}: от`}
          min={min}
          max={max}
          value={low}
          onChange={(e) => onChange(Math.min(Number(e.target.value), high), high)}
        />
        <input
          type="range"
          aria-label={`${title}: до`}
          min={min}
          max={max}
          value={high}
          onChange={(e) => onChange(low, Math.max(Number(e.target.value), low))}
        />
      </div>
    </fieldset>
  );
}
